import React from "react";
import { ChoiceCarousel, InfoCard, ConsequenceBlock, WarningCard, getTextContent } from ".";
import "../App.css";
import { ChapterBeat, Choice, ContentType } from "../story";
import titleFooter from "../assets/divider-brown.png"
import textPlaceholder from "../assets/text-placeholder.png"

/**
 * Parameters expected by a Section. 
 * @param {ChapterBeat} storyBeat - The ChapterBeat object passed to the section. The UI element type must be decided in the Section component,
 * so that the content displays in the correct format each time the GamePage re-renders.
 * @param {scrollId} scrollId - If this element is the most recent storyBeat, scrolling behavior will wait for the element with this ID to load before starting.
 * @param {function} onChoiceSelected - Called and/or passed to a Choice object, if storyBeat is a Choice.
*/
export interface SectionProps {
    storyBeat: ChapterBeat
    scrollId: string
    onChoiceSelected: () => void;
}

/**
 * Parses the UI element type and applies a fade-in animation on render.
 * @param {SectionProps} props - Parameters expected by a Section. 
 */
export const Section = (props: SectionProps) => {
    const { storyBeat, onChoiceSelected, scrollId } = props;
    const parseChild = () => {
        switch(storyBeat.contentType) {
            case ContentType.IMAGE:
                return (
                    <div className="fade-edges">
                        <img id={scrollId} src={require(`../assets/images/${storyBeat.content as string}`)} alt={`Story illustration: ${storyBeat.content as string}`}/>
                    </div>
                );
            case ContentType.CHOICE:
                return (<ChoiceCarousel scrollId={scrollId} onChoiceSelected={onChoiceSelected} choices={storyBeat.content as Choice[]}/>);
            case ContentType.TITLE:
                return (
                    <div className="header-section">
                        <h1>{storyBeat.content as string}</h1>
                        <h3>{storyBeat.extraContent as string}</h3>
                        <img id={scrollId} src={titleFooter} alt=""/>
                        {storyBeat.beatMedia &&<img style={{width: "125px", height: "120px"}} id={`media-${scrollId}`} src={require(`../assets/images/${storyBeat.beatMedia}`)} alt=""/>}
                    </div>
                );
            case ContentType.INFO:
                return (<InfoCard scrollId={scrollId} content={storyBeat.content as string}/>);
            case ContentType.CONSEQUENCE:
                if (parseInt(storyBeat.content as string) === 0) {
                    return(<></>);
                }
                return ( //for some reason, the css doesn't change the padding for these; possibly a consequence of the inline overrwrite?
                    <div className="modifier-section"> 
                        <ConsequenceBlock choice={storyBeat.content as Choice} scrollId={scrollId}/>
                    </div>
                );
            case ContentType.WARNING:
                return(<WarningCard scrollId={scrollId} content={storyBeat.content as string}/>);
            case ContentType.SMALL_TITLE:
                return (
                    <h2 className="small-title-section">
                        {storyBeat.content as string}
                        <img id={scrollId} src={textPlaceholder} alt=""/>
                    </h2>
                    );
            default: 
            //onload doesn't seem to account for the height of a <p> element. We can make scroll behavior consistent with other content by attaching a dummy image with a scrollId.          
                return (
                    <div className="text-section">
                        <img id={scrollId} src={textPlaceholder} alt=""/>
                        {getTextContent(storyBeat.content as string | string[])}
                    </div>
                );
        }
    }

    return (
        <div className='fade-in'>
            {parseChild()}
        </div>
    );
}